/**
 * Google Places API (New) — fetch a short business blurb: `editorialSummary` when present,
 * else `generativeSummary.overview` (Gemini). If you surface the latter, Google requires
 * in-UI attribution — see place-summaries policy.
 *
 * Env: REACT_APP_GOOGLE_PLACES_API_KEY or REACT_APP_GOOGLE_API_KEY (browser key;
 * enable Places API (New) and allow your app origin in GCP).
 */

import { formFieldNames } from 'framework/lib/WebstoreAddressService/WebstoreAddressService';

const SEARCH_URL = 'https://places.googleapis.com/v1/places:searchText';
const LOG = '[GooglePlaces]';

function getPlacesApiKey() {
    return (
        process.env.REACT_APP_GOOGLE_PLACES_API_KEY || process.env.REACT_APP_GOOGLE_API_KEY || ''
    );
}

function placeDetailsUrl(placeResourceName) {
    const id = String(placeResourceName).replace(/^places\//, '');
    return `https://places.googleapis.com/v1/places/${encodeURIComponent(id)}`;
}

export function buildCategoryQueryFromIds(vendorCategoryId, categoryList) {
    if (!vendorCategoryId || !Array.isArray(categoryList) || categoryList.length === 0) {
        return '';
    }
    const ids = String(vendorCategoryId)
        .split(',')
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => !Number.isNaN(n));
    const labels = ids.map((id) => categoryList.find((c) => c.id === id)?.name).filter(Boolean);
    return labels.join(' ').trim();
}

function extractEditorialSummary(place) {
    const es = place?.editorialSummary;
    if (!es) return null;
    let raw = null;
    if (typeof es === 'string') raw = es;
    else if (typeof es.text === 'string') raw = es.text;
    const t = raw != null ? String(raw).trim() : '';
    return t || null;
}

/** Gemini AI place summary (Places API New). UI must show attribution when displaying this text. */
function extractGenerativeOverview(place) {
    const ov = place?.generativeSummary?.overview;
    if (!ov) return null;
    if (typeof ov === 'string') return ov.trim() || null;
    if (typeof ov.text === 'string') return ov.text.trim() || null;
    return null;
}

/** Prefer editorialSummary; else generativeSummary.overview (AI). */
function extractBusinessDescriptionFromPlace(place) {
    return extractEditorialSummary(place) || extractGenerativeOverview(place) || null;
}

async function searchText(apiKey, textQuery) {
    const res = await fetch(SEARCH_URL, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-Goog-Api-Key': apiKey,
            'X-Goog-FieldMask':
                'places.id,places.name,places.displayName,places.formattedAddress,places.editorialSummary,places.generativeSummary',
        },
        body: JSON.stringify({
            textQuery,
            maxResultCount: 20,
        }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        const msg = data.error?.message || res.statusText || 'Unknown error';
        throw new Error(`Places searchText HTTP ${res.status}: ${msg}`);
    }
    return data;
}

async function getPlaceDetails(apiKey, placeResourceName) {
    const url = placeDetailsUrl(placeResourceName);
    const res = await fetch(url, {
        method: 'GET',
        headers: {
            'X-Goog-Api-Key': apiKey,
            'X-Goog-FieldMask': 'id,displayName,editorialSummary,generativeSummary',
        },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        const msg = data.error?.message || res.statusText || 'Unknown error';
        throw new Error(`Places place details HTTP ${res.status}: ${msg}`);
    }
    return data;
}

async function editorialSummaryFromSearchHit(apiKey, place) {
    let description = extractBusinessDescriptionFromPlace(place);

    if (!description && place.name) {
        try {
            const details = await getPlaceDetails(apiKey, place.name);
            description = extractBusinessDescriptionFromPlace(details);
        } catch (e) {
            console.error(`${LOG} getPlaceDetails failed`, e?.message || e);
        }
    }

    return description?.trim() || null;
}

/**
 * @param {{ businessName: string, categoryQuery?: string, locationHint?: string }} params
 * @returns {Promise<string|null>}
 */
export async function fetchGooglePlacesEditorialSummary({
    businessName,
    categoryQuery = '',
    locationHint = '',
}) {
    const apiKey = getPlacesApiKey();
    if (!apiKey) {
        console.warn(
            `${LOG} Missing REACT_APP_GOOGLE_PLACES_API_KEY or REACT_APP_GOOGLE_API_KEY — cannot fetch business description.`
        );
        return null;
    }
    if (!businessName?.trim()) {
        return null;
    }

    const textQuery = [businessName.trim(), categoryQuery?.trim(), locationHint?.trim()]
        .filter(Boolean)
        .join(' ')
        .trim();

    let searchData;
    try {
        searchData = await searchText(apiKey, textQuery);
    } catch (e) {
        console.error(`${LOG} searchText failed`, e?.message || e);
        return null;
    }

    const places = searchData.places || [];
    if (places.length === 0) {
        return null;
    }

    const tryCount = Math.min(places.length, 10);
    for (let i = 0; i < tryCount; i++) {
        const finalDesc = await editorialSummaryFromSearchHit(apiKey, places[i]);
        if (finalDesc?.trim()) {
            return finalDesc.trim();
        }
    }

    return null;
}

function buildLocationHintFromForm(formData) {
    if (!formData || typeof formData !== 'object') {
        return '';
    }
    const serviceLine = [
        formData[formFieldNames.ServiceAddressStreet],
        formData[formFieldNames.ServiceAddressCity],
        formData[formFieldNames.ServiceAddressState],
        formData[formFieldNames.ServiceAddressZipcode],
    ]
        .filter(Boolean)
        .join(' ')
        .trim();
    if (serviceLine) {
        return serviceLine;
    }
    return [
        formData[formFieldNames.BusinessAddressStreet1],
        formData[formFieldNames.BusinessAddressStreet2],
        formData[formFieldNames.BusinessAddressCity],
        formData[formFieldNames.BusinessAddressState],
        formData[formFieldNames.BusinessAddressZipcode],
    ]
        .filter(Boolean)
        .join(' ')
        .trim();
}

function applyDescriptionPatchToWebstore(webstore, patch, trimmedDescription) {
    if (!patch.data) {
        console.warn(
            `${LOG} PATCH ok but response body empty — local BusinessSummary was set; not calling setFromApi (avoid wiping state).`
        );
        return;
    }
    const serverDesc = patch.data.businessDescription;
    const hasServerDesc = serverDesc != null && String(serverDesc).trim() !== '';
    webstore.setFromApi({
        ...patch.data,
        businessDescription: hasServerDesc ? serverDesc : trimmedDescription,
    });
}

/**
 * After Pearl Connect business step: fetch Google editorial summary and PATCH webstore
 * if `businessDescription` is still empty. Runs fully async — do not await from UI flow.
 *
 * @param {{ formData: object, categoryList: Array<{id:number,name:string}>, webstore: object, patchFunction: (data: object) => Promise<{ok: boolean, data?: object}> }} params
 */
export async function patchBusinessDescriptionFromGoogleAfterBusinessStep({
    formData,
    categoryList,
    webstore,
    patchFunction,
}) {
    try {
        const businessName = formData?.WebstoreName?.trim();
        if (!businessName) {
            return;
        }

        if (webstore.BusinessSummary?.trim()) {
            return;
        }

        const categoryQuery = buildCategoryQueryFromIds(formData.VendorCategoryId, categoryList);
        const locationHint = buildLocationHintFromForm(formData);

        const description = await fetchGooglePlacesEditorialSummary({
            businessName,
            categoryQuery,
            locationHint,
        });

        if (!description?.trim()) {
            return;
        }

        if (webstore.BusinessSummary?.trim()) {
            return;
        }

        const trimmedDescription = description.trim();
        webstore.updateWebstore({ BusinessSummary: trimmedDescription });

        const patch = await patchFunction(webstore.forApi);

        if (!patch.ok) {
            console.error(`${LOG} PATCH webstore failed`, {
                status: patch?.status,
                problem: patch?.problem,
            });
            return;
        }
        applyDescriptionPatchToWebstore(webstore, patch, trimmedDescription);
    } catch (err) {
        console.error(`${LOG} background job failed`, err?.message || err, err);
    }
}
